import { useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  UserCredential,
} from 'firebase/auth';
import { auth } from '../config/firebase';

interface AuthError {
  code: string;
  message: string;
}

export const useAuth = () => {
  const [error, setError] = useState<AuthError | null>(null);
  const [loading, setLoading] = useState(false);

  const signup = async (
    email: string,
    password: string,
    displayName: string
  ): Promise<UserCredential | null> => {
    try {
      setLoading(true);
      setError(null);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(result.user, { displayName });
      return result;
    } catch (err: any) {
      setError({
        code: err.code,
        message: err.message,
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const login = async (
    email: string,
    password: string
  ): Promise<UserCredential | null> => {
    try {
      setLoading(true);
      setError(null);
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      setError({
        code: err.code,
        message: err.message,
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await signOut(auth);
    } catch (err: any) {
      setError({
        code: err.code,
        message: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err: any) {
      setError({
        code: err.code,
        message: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    error,
    loading,
    signup,
    login,
    logout,
    resetPassword,
  };
}; 